// /var/www/oszczedzam/client/src/pages/Calendar.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, useTheme, Button, Dialog,
  DialogContent, Snackbar, Alert, CircularProgress, Tabs, Tab
} from '@mui/material';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/pl';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Add as AddIcon } from '@mui/icons-material';
import { useTransactions } from '../hooks/useTransactions';
import TransactionForm from '../components/transactions/TransactionForm';
import TransactionDetails from '../components/calendar/TransactionDetails';
import FinancialForecast from '../components/calendar/FinancialForecast';

// Skonfiguruj lokalizer dla kalendarza z językiem polskim
moment.locale('pl');
const localizer = momentLocalizer(moment);

// Niestandardowe widoki kalendarza
const calendarViews = {
  month: true,
  week: true,
  day: true,
  agenda: true
};

function Calendar() {
  const theme = useTheme();
  const { transactions, loading, error, addTransaction, updateTransaction, deleteTransaction } = useTransactions();
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [openAddForm, setOpenAddForm] = useState(false);
  const [openEventDetails, setOpenEventDetails] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });
  const [tabValue, setTabValue] = useState(0); // 0: Kalendarz, 1: Prognozy

  // Przekształć transakcje na eventy kalendarza
  useEffect(() => {
    if (transactions && transactions.length > 0) {
      const events = transactions.map(transaction => {
        const start = new Date(transaction.date);
        const end = new Date(transaction.date);
        
        // Dla lepszej wizualizacji dodaj kilka godzin do końca
        end.setHours(end.getHours() + 2);
        
        return {
          id: transaction.id,
          title: transaction.title,
          start: start,
          end: end,
          allDay: true,
          resource: transaction
        };
      });
      setCalendarEvents(events);
    } else {
      setCalendarEvents([]);
    }
  }, [transactions]);

  // Obsługa kliknięcia w komórkę kalendarza (pusty obszar)
  const handleSelectSlot = (slotInfo) => {
    setSelectedDate(slotInfo.start);
    setOpenAddForm(true);
  };

  // Obsługa kliknięcia w event
  const handleSelectEvent = (event) => {
    setSelectedEvent(event.resource);
    setOpenEventDetails(true);
  };

  // Obsługa dodawania nowej transakcji
  const handleAddTransaction = async (transactionData) => {
    try {
      // Jeśli wybrano datę z kalendarza, użyj jej
      if (selectedDate) {
        const formattedDate = moment(selectedDate).format('YYYY-MM-DD');
        transactionData.date = formattedDate;
      }
      
      await addTransaction(transactionData);
      setOpenAddForm(false);
      showNotification('Transakcja została dodana pomyślnie!', 'success');
    } catch (error) {
      console.error('Błąd podczas dodawania transakcji:', error);
      showNotification('Nie udało się dodać transakcji. Spróbuj ponownie później.', 'error');
    }
  };

  // Obsługa aktualizacji transakcji
  const handleUpdateTransaction = async (transactionData) => {
    try {
      await updateTransaction(selectedEvent.id, transactionData);
      setOpenEventDetails(false);
      setSelectedEvent(null);
      showNotification('Transakcja została zaktualizowana pomyślnie!', 'success');
    } catch (error) {
      console.error('Błąd podczas aktualizacji transakcji:', error);
      showNotification('Nie udało się zaktualizować transakcji. Spróbuj ponownie później.', 'error');
    }
  };

  // Obsługa usuwania transakcji
  const handleDeleteTransaction = async (transactionId) => {
    try {
      await deleteTransaction(transactionId);
      setOpenEventDetails(false);
      setSelectedEvent(null);
      showNotification('Transakcja została usunięta pomyślnie!', 'success');
    } catch (error) {
      console.error('Błąd podczas usuwania transakcji:', error);
      showNotification('Nie udało się usunąć transakcji. Spróbuj ponownie później.', 'error');
    }
  };

  // Wyświetl powiadomienie
  const showNotification = (message, type) => {
    setNotification({
      open: true,
      message,
      type
    });
  };

  // Zamknij powiadomienie
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Style dla eventów w kalendarzu w zależności od typu transakcji
  const eventStyleGetter = (event) => {
    const transaction = event.resource;
    const isExpense = transaction.amount < 0;
    
    const backgroundColor = isExpense 
      ? theme.palette.error.main 
      : theme.palette.success.main;
    
    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.8,
        color: '#fff',
        border: '0px',
        display: 'block'
      }
    };
  };

  // Własne komponenty dla headerów kalendarza
  const customDayHeaderFormat = (date, culture, localizer) => {
    return localizer.format(date, 'dddd', culture);
  };

  // Format etykiety daty w widoku agendy
  const agendaDateFormat = (date, culture, localizer) => {
    return localizer.format(date, 'DD MMMM YYYY', culture);
  };

  // Napisy w kalendarzu
  const messages = {
    today: 'Dzisiaj',
    previous: 'Poprzedni',
    next: 'Następny',
    month: 'Miesiąc',
    week: 'Tydzień',
    day: 'Dzień',
    agenda: 'Agenda',
    date: 'Data',
    time: 'Czas',
    event: 'Wydarzenie',
    allDay: 'Cały dzień',
    noEventsInRange: 'Brak transakcji w tym okresie'
  };
  
  // Obsługa zmiany zakładki
  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">Kalendarz</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedDate(new Date());
            setOpenAddForm(true);
          }}
        >
          Nowa transakcja
        </Button>
      </Box>
      
      {/* Komunikat o błędzie */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Zakładki */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleChangeTab}>
          <Tab label="Kalendarz" />
          <Tab label="Prognozy finansowe" />
        </Tabs>
      </Box>
      
      {/* Panel z kalendarzem */}
      {tabValue === 0 && (
        <Paper elevation={2} sx={{ height: 'calc(100vh - 200px)', minHeight: '500px', p: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <BigCalendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              views={calendarViews}
              selectable
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              eventPropGetter={eventStyleGetter}
              dayHeaderFormat={customDayHeaderFormat}
              formats={{
                agendaDateFormat: agendaDateFormat
              }}
              messages={messages}
              defaultView="month"
              popup
            />
          )}
        </Paper>
      )}
      
      {/* Panel z prognozami finansowymi */}
      {tabValue === 1 && (
        <FinancialForecast numberOfMonths={6} />
      )}
      
      {/* Dialog z formularzem dodawania transakcji */}
      <Dialog open={openAddForm} onClose={() => setOpenAddForm(false)} maxWidth="md" fullWidth>
        <DialogContent>
          <TransactionForm 
            onSubmit={handleAddTransaction}
            initialData={{ date: selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD') }}
          />
        </DialogContent>
      </Dialog>
      
      {/* Dialog ze szczegółami wydarzenia (transakcji) */}
      {selectedEvent && (
        <Dialog open={openEventDetails} onClose={() => setOpenEventDetails(false)} maxWidth="md" fullWidth>
          <DialogContent>
            <TransactionDetails 
              transaction={selectedEvent}
              onEdit={(transaction) => {
                setOpenEventDetails(false);
                setSelectedEvent(transaction);
                setOpenAddForm(true);
              }}
              onDelete={handleDeleteTransaction}
              onClose={() => setOpenEventDetails(false)}
            />
          </DialogContent>
        </Dialog>
      )}
      
      {/* Powiadomienia */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.type} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Calendar;